import styled, { css } from 'styled-components'
import {
  color,
  space,
  typography,
  display,
  width,
  ColorProps,
  SpaceProps,
  TypographyProps,
  DisplayProps,
  WidthProps,
} from 'styled-system'
import { Link as ReactRouterLink } from 'gatsby'

import { WhiteSpaceProps, whiteSpace } from '@styles/styledSystem'
import { TColors } from '@styles/variables/colors'
import { getColor } from '@styles/helpers'
import { above } from '@styles/mixins/breakpoints'

export type TTypography = ColorProps & SpaceProps & TypographyProps & DisplayProps & WhiteSpaceProps & WidthProps

export interface IWithLinkProps extends TTypography {
  hoverColor?: TColors
}

export const withTypography = css<TTypography>`
  display: block;
  margin-top: 0;

  ${space}
  ${color}
  ${typography}
  ${display}
  ${whiteSpace}
  ${width}
`

export const withTypographyDefault = css`
  font-style: normal;
  font-weight: 400;
  font-size: ${({ theme }) => theme.typography.baseFontSize}px;
  line-height: ${({ theme }) => theme.typography.baseLineHeight}px;
  font-family: ${({ theme }) => theme.typography.defaultFontFamily};
`

export const withTypographySmall = css`
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  font-family: ${({ theme }) => theme.typography.defaultFontFamily};
`

export const withTypographyLarge = css`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  font-family: ${({ theme }) => theme.typography.defaultFontFamily};
  ${above('md')(css`
    font-size: 16px;
    line-height: 24px;
  `)}
`

export const withTypographyDesc = css`
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  font-family: ${({ theme }) => theme.typography.defaultFontFamily};
`

export const withTypographyTag = css`
  font-style: normal;
  font-weight: 400;
  font-size: 10px;
  line-height: 12px;
  font-family: ${({ theme }) => theme.typography.defaultFontFamily};
`

export const withTypographyH1 = css`
  font-style: normal;
  font-weight: 400;
  font-size: 24px;
  line-height: 28px;
  font-family: ${({ theme }) => theme.typography.headingFontFamily};

  ${above('md')(css`
    font-size: 32px;
    line-height: 40px;
  `)}
  ${above('xl')(css`
    font-size: 36px;
    line-height: 44px;
  `)}
`

export const withTypographyH2 = css`
  font-style: normal;
  font-weight: 400;
  font-size: 22px;
  line-height: 24px;
  font-family: ${({ theme }) => theme.typography.defaultFontFamily};

  ${above('md')(css`
    font-size: 24px;
    line-height: 32px;
  `)}

  ${above('xl')(css`
    font-size: 32px;
    line-height: 40px;
  `)}
`

export const withTypographyH3 = css`
  font-style: normal;
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  font-family: ${({ theme }) => theme.typography.defaultFontFamily};

  ${above('md')(css`
    font-size: 20px;
    line-height: 24px;
  `)}

  ${above('xl')(css`
    font-size: 24px;
    font-weight: 400;
    line-height: 28px;
  `)}
`

export const withTypographyH4 = css`
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  font-family: ${({ theme }) => theme.typography.defaultFontFamily};
  ${above('md')(css`
    font-size: 20px;
    line-height: 24px;
  `)}
`

export const withTypographyParagraph = css`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  font-family: ${({ theme }) => theme.typography.defaultFontFamily};

  ${above('md')(css`
    font-size: 16px;
    line-height: 24px;
  `)}
`

export const withTypographyDisclaimer = css`
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.darkGray};
  font-family: ${({ theme }) => theme.typography.defaultFontFamily};
  ${above('md')(css`
    font-size: 14px;
    line-height: 20px;
  `)}
`

export const withLink = css<IWithLinkProps>`
  cursor: pointer;
  transition: 0.15s ease-in color;
  text-decoration: none;

  &:hover {
    text-decoration: none;
    color: ${(props) => (props.hoverColor ? getColor(props.hoverColor) : 'inherit')};
  }

  ${withTypography};
`

/**
 * Стилизованный базовый компонент типографии
 * Использовать, если нет нужных вариантов в компонентах типографии
 */
export const STypography = styled.span<TTypography>`
  ${withTypography};
`

/**
 * ## Стилизованный компонент (Typography default)
 * - В дизайне: **Lk desktop/14 text**
 */
export const STypographyDefault = styled.span<TTypography>`
  ${withTypographyDefault};
  ${withTypography};
`
export const SDisclaimer = styled.span<TTypography>`
  ${withTypographyDisclaimer};
  ${withTypography};
`

/**
 * ## Стилизованный компонент (Typography small)
 * - В дизайне: **SB/13 text** или **Lk tablet/ 13 text**
 */
export const STypographySmall = styled.span<TTypography>`
  ${withTypographySmall};
  ${withTypography};
`

/**
 * ## Стилизованный компонент (Typography large)
 * - В дизайне: **Lk desktop/ H4** или Lk tablet/16 text
 */
export const STypographyLarge = styled.span<TTypography>`
  ${withTypographyLarge};
  ${withTypography};
`

/**
 * ## Стилизованный компонент (Typography desc)
 * - В дизайне: **SB/12 text** или **Description- 12**
 */
export const STypographyDesc = styled.span<TTypography>`
  ${withTypographyDesc};
  ${withTypography};
`

/**
 * ## Стилизованный компонент (tag)
 * - В дизайне:**SB/tag**
 */
export const STypographyTag = styled.span<TTypography>`
  ${withTypographyTag};
  ${withTypography};
`

export const SH1 = styled.h1<TTypography>`
  ${withTypographyH1};
  ${withTypography};
`

export const SH2 = styled.h2<TTypography>`
  ${withTypographyH2};
  ${withTypography};
`

export const SH3 = styled.h3<TTypography>`
  ${withTypographyH3};
  ${withTypography};
`

export const SH4 = styled.h4<TTypography>`
  ${withTypographyH4};
  ${withTypography};
`

export const SLink = styled.a<IWithLinkProps & TTypography>`
  ${withLink}
  ${withTypography};
`

export const SRouterLink = styled(ReactRouterLink)<IWithLinkProps & TTypography>`
  ${withLink}
  ${withTypography};
`

export const STypographyParagraph = styled.span<TTypography>`
  ${withTypographyParagraph};
  ${withTypography};
`

export const SParagraph = styled.p<TTypography>`
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  font-family: ${({ theme }) => theme.typography.defaultFontFamily};

  ${withTypography};
  ${space}
`
